import { useMemo } from 'react';
import { formatDateOnly } from '../lib/dateUtils';
import type { UserDraft } from '@gng-ehs/shared';

interface DraftRecoveryBannerProps {
  draft: UserDraft | null;
  onRestore: () => void;
  onDiscard: () => void;
}

function formatRelativeTime(dateStr: string): string {
  try {
    const time = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`).getTime();
    const diffSec = Math.floor((Date.now() - time) / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateOnly(dateStr);
  } catch {
    return 'recently';
  }
}

export function DraftRecoveryBanner({ draft, onRestore, onDiscard }: DraftRecoveryBannerProps) {
  const timeAgo = useMemo(() => {
    return draft?.updated_at ? formatRelativeTime(draft.updated_at) : '';
  }, [draft?.updated_at]);

  if (!draft) return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg p-3 sm:p-3.5 mb-4 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="text-xl leading-none flex-shrink-0 mt-0.5">💾</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                Unsaved Draft Found
              </span>
              <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                Saved {timeAgo}
              </span>
            </div>
            <p className="text-xs text-amber-800 mt-0.5 truncate font-medium">
              "{draft.title}" {draft.subtitle ? `• ${draft.subtitle}` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-center">
          <button
            type="button"
            onClick={onDiscard}
            className="text-xs text-amber-800 hover:text-red-700 px-2.5 py-1.5 rounded hover:bg-amber-100/80 transition-colors font-medium cursor-pointer"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={onRestore}
            className="btn bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-medium text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 shadow-sm cursor-pointer transition-all"
          >
            <span>↩</span>
            <span>Resume Draft</span>
          </button>
        </div>
      </div>
    </div>
  );
}
